/*
 * Copyright 2026 Sony Corporation
 */

import AddIcon from '@mui/icons-material/Add';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Box, Button, Divider, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import type { Device, DeviceStateResponse } from '@ips-device-verse/twin-service/schema';

import { PanelHead } from './PanelHead';

export interface DeviceListPanelProps {
  devices: Device[];
  selectedId: string | undefined;
  /** Live shadows, keyed by device; absent until one arrives. */
  states: Record<string, DeviceStateResponse>;
  onSelect: (deviceId: string) => void;
  onAdd: () => void;
  onHide: () => void;
}

/**
 * Dimmed means no gateway has this device, so nothing written here reaches
 * anything yet. A device with a gateway but no PTP client attached is idle, not
 * unreachable, and stays lit.
 */
const connectedness = (state: DeviceStateResponse | undefined) => {
  const reported = state?.state.reported;
  if (!reported) {
    return { dim: true, title: 'No gateway has reported for this device yet.' };
  }
  const { connected, client, since } = reported.session;
  if (!connected) {
    return { dim: false, title: client ? `Idle. Last used by ${client}.` : 'Idle. No client attached.' };
  }
  const from = since ? ` since ${new Date(since * 1000).toLocaleTimeString()}` : '';
  return { dim: false, title: `In use by ${client ?? 'a client'}${from}.` };
};

export const DeviceListPanel = ({ devices, selectedId, states, onSelect, onAdd, onHide }: DeviceListPanelProps) => (
  <Box className="flex h-full flex-col">
    <PanelHead title="Devices" onHide={onHide} />

    {devices.length === 0 ? (
      <Typography variant="body2" color="text.secondary" className="px-4 py-3">
        No devices yet.
      </Typography>
    ) : (
      <List dense className="flex-1 overflow-y-auto">
        {devices.map((device) => {
          const live = connectedness(states[device.device_id]);
          return (
            <ListItemButton
              key={device.device_id}
              selected={device.device_id === selectedId}
              onClick={() => onSelect(device.device_id)}
              title={live.title}
              className={`py-0.5 ${live.dim ? 'opacity-60' : ''}`}
            >
              <ListItemIcon className="min-w-9">
                <VideocamIcon fontSize="small" color={live.dim ? 'disabled' : 'success'} />
              </ListItemIcon>
              <ListItemText primary={device.name} className="my-0" />
            </ListItemButton>
          );
        })}
      </List>
    )}

    <Box className="flex-1" />
    <Divider />
    <Box className="p-3">
      <Button startIcon={<AddIcon />} onClick={onAdd} fullWidth size="small">
        Add device
      </Button>
    </Box>
  </Box>
);
